import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from "react-i18next";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'

import SimpleToggle from '../../Common/Tailwind/Toggles/SimpleToggle';
import Dropdown from '../../Common/Tailwind/SelectMenus/Dropdown';
import MultiGradeBatchFilter from '../../Batches/MultiGradeBatchFilter';
import SyllabusFilter from '../../Syllabus/SyllabusFilter';
import Spinner from '../../Common/Tailwind/Spinner';
import AutoTestCreate from '../AutoTestCreate';
import { FULL_TEST_TYPES, TEST_CREATE_TYPE_BASIC, securedCreateAutomatedTest, securedCreateTest, securedCreateTestSeries, securedCreateTestUsers, securedDeleteTestUsers, securedFetchAllTestSeries, securedUpdateTest } from '../../../services/TestService';
import { UnauthorizedError } from '../../../services/Errors';
import { ADMIN_USER_TYPE, securedFetchUsers } from '../../../services/UserService';
import { navigateCallbackOptions } from '../../../services/AuthenticationService';

import { FiFilePlus } from "react-icons/fi";
import SelectionDropdownMultiple from '../../Common/SelectionDropdownMultiple';
import { checkArraysEqual, getUniqueValues } from '../../../services/CommonService';
import AutoTestSectionData from './AutoTestSectionData';
import { securedFetchTagsByFilter } from '../../../services/TagService';
import { ArrowUpTrayIcon, CheckIcon, DocumentPlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

const TestUpsert = ({ isOpen, onRequestClose, onTestUpsert = null, testData = null, user = ADMIN_USER_TYPE }) => {
    const [testName, setTestName] = useState(testData && testData.test_name ? testData.test_name : '');
    const [allTestSeries, setAllTestSeries] = useState([]);
    const [testSeries, setTestSeries] = useState(testData && testData.test_series_id ? { id: testData.test_series_id } : {});
    const [testType, setTestType] = useState(testData && testData.test_type ? FULL_TEST_TYPES.find(entry => entry.id === testData.test_type) || FULL_TEST_TYPES[0] : FULL_TEST_TYPES[0]);
    const [grades, setGrades] = useState([]);
    const [selectedGrades, setSelectedGrades] = useState([]);
    const [batches, setBatches] = useState([]);
    const [selectedBatches, setSelectedBatches] = useState(testData && testData.batches ? testData.batches.map(batch => ({ id: batch.batch_id })) : []);
    const [students, setStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState(testData && testData.users ? testData.users.map(user => ({ id: user.user_id })) : []);
    const [testDurationMinutes, setTestDurationMinutes] = useState(testData && testData.test_duration_minutes ? testData.test_duration_minutes : 0);
    const [testDateTime, setTestDateTime] = useState(testData && testData.test_start_time ? testData.test_start_time : '')
    const [isAutomatedTest, setAutomatedTest] = useState(false);

    const [subjects, setSubjects] = useState([]);
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [topicGrades, setTopicGrades] = useState([]);
    const [selectedTopicGrades, setSelectedTopicGrades] = useState([]);
    const [mainTopics, setMainTopics] = useState([]);
    const [selectedMainTopics, setSelectedMainTopics] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [tags, setTags] = useState([]);
    const [isEntireSyllabus, setEntireSyllabus] = useState(true);
    const [isExcludePrevTests, setExcludePrevTests] = useState(false);
    const [testDifficulty, setTestDifficulty] = useState(0.5);
    const [selectedTags, setSelectedTags] = useState([]);

    const [subjectsData, setSubjectsData] = useState([]);
    const [sectionsData, setSectionsData] = useState([]);

    const [isSubmitted, setSubmitted] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const fetchAndSetAllTestSeries = async () => {
        const allTestSeriesResponse = await securedFetchAllTestSeries(navigateCallbackOptions(navigate));
        if (allTestSeriesResponse === null) {
            return null;
        }
        return allTestSeriesResponse.data;
    };

    useEffect(() => {
        (async () => {
            if (!isOpen) {
                return;
            }
            const testSeriesList = await fetchAndSetAllTestSeries();
            if (testSeriesList === null) {
                return;
            }
            setAllTestSeries(testSeriesList);
            setTestSeries(testSeriesList.find(series => series.id === testSeries.id) || {});
            const allStudents = await securedFetchUsers('students', {}, navigateCallbackOptions(navigate));
            if (allStudents === null) {
                return;
            }
            const studentEntries = allStudents.data.map(student => ({ name: `${student.user_data.first_name} ${student.user_data.last_name}`, id: student.id }));
            setStudents(studentEntries);
            const newSelectedStudents = testData && testData.users ? testData.users.map(user => ({ id: user.user_id })) : [];
            const selectedStudentIds = newSelectedStudents.map(student => student.id);
            setSelectedStudents(studentEntries.filter(student => selectedStudentIds.includes(student.id)));
            const tagsResponse = await securedFetchTagsByFilter({}, navigateCallbackOptions(navigate));
            if (tagsResponse === null) {
                return;
            }
            setTags(tagsResponse.data);
        })();
    }, [isOpen]);


    useEffect(() => {
        if (testData === null) {
            return;
        }
        setTestName(testData.test_name);
        setTestSeries(testData.test_series_id ? { id: testData.test_series_id } : {});
        setTestType(testData.test_type ? FULL_TEST_TYPES.find(entry => entry.id === testData.test_type) || FULL_TEST_TYPES[0] : FULL_TEST_TYPES[0]);
        setSelectedBatches(testData.batches ? testData.batches.map(batch => ({ id: batch.batch_id })) : []);
        setSelectedStudents(testData.users ? testData.users.map(user => ({ id: user.user_id })) : []);
        setTestDurationMinutes(testData.test_duration_minutes ? testData.test_duration_minutes : 0);
        setTestDateTime(testData.test_start_time ? testData.test_start_time : '');
    }, [testData]);

    const onTestSeriesSelectionChange = (selection) => {
        const testSeriesId = selection.value ? selection.value : null;
        if (testSeriesId) {
            setTestSeries({ id: selection.value, test_series_name: selection.label })
        }
    }

    const makeTopicsList = () => {
        if (selectedSubjects.length === 0 && selectedTopicGrades.length === 0 && selectedMainTopics.length === 0 && selectedTopics.length === 0) {
            return null;
        }
        let allSelectedTopics = [];
        if (!topics || !mainTopics) {
            return [];
        }
        if (selectedTopics.length > 0) {
            allSelectedTopics = allSelectedTopics.concat(selectedTopics);
        }
        if (selectedMainTopics.length > 0) {
            const notSelectedMainTopics = selectedMainTopics.filter(mainTopic => selectedTopics.find(topic => topic.parent_id === mainTopic.id) == null);
            allSelectedTopics = allSelectedTopics.concat(notSelectedMainTopics);
        }
        return allSelectedTopics.length === 0 ? topics.concat(mainTopics) : allSelectedTopics;
    }

    const onTestSeriesAdd = async (addition) => {
        const data = {
            'test_series_name': addition.test_series_name
        };
        const response = await securedCreateTestSeries(data, navigateCallbackOptions(navigate));
        if (response === null) {
            return;
        }
        const newItem = { id: response.test_series_id, test_series_name: addition.test_series_name };
        setAllTestSeries([...allTestSeries, newItem]);
        setTestSeries(newItem);

    }

    const onSelectedBatchToggle = (selected) => {
        const selectedIds = selected.map(sel => sel.value);
        const selectedBatchesFromSelect = batches.filter(batch => selectedIds.includes(batch.id));
        setSelectedBatches(selectedBatchesFromSelect);
    };

    const onTestDurationChange = (event) => {
        if (event.target.value < 0) {
            setTestDurationMinutes(0);
        } else {
            setTestDurationMinutes(parseInt(event.target.value));
        }
    }

    const onTestDateTimeChange = (event) => {
        setTestDateTime(event.target.value);
    }

    const onSelectedStudentToggle = async (selected) => {
        const selectedIds = selected.map(sel => sel.value);
        const newStudents = students.filter(student => selectedIds.includes(student.id));
        setSelectedStudents(newStudents);
    };

    const onSelectedTagChange = (selection) => {
        // const { value, checked } = event.target;
        const selectedIds = selection.map(sel => sel.value);
        const newSelectedTags = tags.filter(tag => selectedIds.includes(tag.id));
        setSelectedTags(newSelectedTags);
    };

    const updateUsersData = async (testId) => {
        const oldUserIds = testData && testData.users ? testData.users.map(user => user.user_id) : [];
        const newUserIds = selectedStudents.map(student => student.id);
        const newUsers = newUserIds.map(id => ({ user_id: id }));
        if (checkArraysEqual(oldUserIds, newUserIds)) {
            return true;
        }
        if (oldUserIds.length > 0) {
            const deleteResponse = await securedDeleteTestUsers(testId, { user_ids: oldUserIds }, navigateCallbackOptions(navigate));
            if (deleteResponse === null) {
                return false;
            }
        }
        if (newUserIds.length > 0) {
            const createResponse = await securedCreateTestUsers(testId, { test_users: newUsers }, navigateCallbackOptions(navigate));
            if (createResponse === null) {
                return false;
            }
        }
        return true;
    }

    const makeTestSectionsData = () => {
        const mappedSectionsData = sectionsData.map((data, index) => ({
            subject_id: parseInt(data.subject_id),
            number_of_questions: parseInt(data.number_of_questions),
            question_type: data.type.id === 'paragraph' ? 'paragraph' : 'standalone',
            question_subtype: data.type.id === 'paragraph' ? data.sub_type.id : data.type.id,
            test_section: {
                full_marks: parseInt(data.full_marks),
                partial_marks: parseInt(data.partial_marks),
                negative_marks: parseInt(data.negative_marks),
            }
        }));
        const subjectWiseSections = {};
        mappedSectionsData.forEach(data => {
            const dataSubjectId = data.subject_id;
            if (subjectWiseSections[dataSubjectId] == null) {
                subjectWiseSections[dataSubjectId] = [];
            }
            delete data['subject_id'];
            subjectWiseSections[dataSubjectId].push({ ...data, test_section: { section_order: subjectWiseSections[dataSubjectId].length + 1, ...data.test_section } });
        });
        return Object.keys(subjectWiseSections).map((subjectId, index) => ({
            test_subject: { subject_id: parseInt(subjectId), subject_order: index + 1 }, test_sections: subjectWiseSections[subjectId]
        }));
    }

    const checkIfDataValid = () => {
        if (testName.length === 0) {
            alert('Test Name is not mentioned!');
            return false;
        }
        if (selectedBatches.length === 0 && isAutomatedTest && (selectedSubjects.length === 0 && selectedTopicGrades.length === 0 && selectedMainTopics.length === 0 && selectedTopics.length === 0)) {
            alert("Atleast one of either batch or topics must be selected. Cannot auto generate test for no batch without any topic assigned!")
            return false;
        }
        if (testDurationMinutes === 0 || testDurationMinutes === null) {
            alert("Test duration must be greater than 0 minutes!");
            return false;
        }

        if (isAutomatedTest) {
            if (subjectsData.length === 0) {
                alert("There should be atleast one subject in auto generated test!");
                return false;
            }

            if (sectionsData.length === 0) {
                alert("There should be atleast one section in auto generated test!");
                return false;
            }
            for (let i = 0; i < sectionsData.length; i++) {
                const sectionData = sectionsData[i];
                if (parseInt(sectionData.number_of_questions) === 0) {
                    alert("Section with 0 questions exists!");
                    return false;
                }
                if (parseInt(sectionData.full_marks) === 0) {
                    alert("Section exists with 0 full marks per question!");
                    return false;
                }
            }
        }
        return true;
    }


    const createBasicTestData = () => {
        const data = {
            test_name: testName,
            test_batches: selectedBatches.map((item) => ({ batch_id: item.id })),
            test_type: testType.id,
            test_duration_minutes: testDurationMinutes
        }
        if (testSeries && testSeries.id) {
            data['test_series_id'] = testSeries.id;
        }
        if (testDateTime.length > 0) {
            data['test_start_time'] = testDateTime;
        }
        return data;
    }

    const createAutomatedTestData = (basicTestData) => {
        const data = { test: { ...basicTestData, difficulty: parseFloat(testDifficulty) } };
        const topicsToTakeFrom = makeTopicsList();
        const tagsToTakeFrom = selectedTags.length > 0 ? selectedTags : null;
        if (topicsToTakeFrom !== null) {
            data['topic_ids'] = topicsToTakeFrom.map(topic => topic.id);
        }
        if (tagsToTakeFrom !== null) {
            data['tag_ids'] = tagsToTakeFrom.map(tag => tag.id);
        }
        data['test_subjects'] = makeTestSectionsData();
        data['exclude_test_questions'] = isExcludePrevTests;
        return data;

    }

    const handleSubmit = async () => {
        setSubmitted(true);

        const isDataValid = checkIfDataValid();
        if (!isDataValid) {
            setSubmitted(false);
            return;
        }
        let response = null;
        const basicTestData = createBasicTestData();
        if (!testData) {
            if (!isAutomatedTest) {
                response = await securedCreateTest(TEST_CREATE_TYPE_BASIC, basicTestData, navigateCallbackOptions(navigate));
            } else {
                response = await securedCreateAutomatedTest(TEST_CREATE_TYPE_BASIC, createAutomatedTestData(basicTestData), navigateCallbackOptions(navigate));
            }
        } else {
            response = await securedUpdateTest(testData.id, basicTestData, navigateCallbackOptions(navigate));
        }
        if (response === null) {
            setSubmitted(false);
            return;
        }
        const upsertTestId = testData && testData.id ? testData.id : response.test_id;
        const updateUsersSuccess = await updateUsersData(upsertTestId);
        if (!updateUsersSuccess) {
            setSubmitted(false);
            return;
        }
        setSubmitted(false);
        onRequestClose();
        if (onTestUpsert) {
            await onTestUpsert(testData ? testData.id : response.test_id);
        }
    }

    const onSubmit = (event) => {
        event.preventDefault();
    };

    const renderTestCreate = () => {
        if (isSubmitted) {
            return <Spinner />;
        }
        return (
            <div>
                {/* <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <DocumentPlusIcon aria-hidden="true" className="h-6 w-6 text-green-600" />
                </div> */}

                <div className="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        {testData ?
                            t('editTest')
                            :
                            t('createNewTest')
                        }
                    </DialogTitle>

                    <div className="mt-5 sm:mx-auto w-full">
                        <form className="space-y-4 overflow-y-auto" onSubmit={onSubmit} >

                            <MultiGradeBatchFilter
                                grades={grades} setGrades={setGrades}
                                selectedGrades={selectedGrades} setSelectedGrades={setSelectedGrades}
                                batches={batches} setBatches={setBatches}
                                selectedBatches={selectedBatches} setSelectedBatches={setSelectedBatches}
                                onSelectedBatchesToggle={onSelectedBatchToggle}
                                prefillGrades={true}
                            />

                            <div className="flex items-center justify-start gap-2 w-full">
                                <label htmlFor="topicname"
                                    className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 min-w-24 mr-1">
                                    {t('testName')}
                                </label>
                                <div className="ml-2 relative w-full flex flex-1 rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600">
                                    <input
                                        name="topicname"
                                        type="text"
                                        value={testName}
                                        onChange={e => setTestName(e.target.value)}
                                        placeholder="Enter..."
                                        required
                                        className="block w-full flex-1 border-1 bg-transparent py-1.5 px-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm/6"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-start gap-2 w-full">
                                <label className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 min-w-24 mr-1">
                                    {t('series')}
                                </label>
                                <Dropdown
                                    className='flex items-center justify-start gap-2 w-full'
                                    list={allTestSeries}
                                    selected={testSeries}
                                    onSelectionChange={onTestSeriesSelectionChange}
                                    valueField="id"
                                    nameField="test_series_name"
                                    required={true}
                                    isSearchable={true}
                                    isAddable={true}
                                    onAddItem={onTestSeriesAdd}
                                />
                            </div>

                            <div className="flex items-center justify-start gap-2 w-full">
                                <label className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 min-w-24 mr-1">
                                    {t('students')}
                                </label>
                                <Dropdown
                                    className='flex items-center justify-start gap-2 w-full'
                                    list={students}
                                    selected={selectedStudents}
                                    onSelectionChange={onSelectedStudentToggle}
                                    valueField="id"
                                    nameField="name"
                                    required={true}
                                    isSearchable={true}
                                />
                            </div>

                            <div className="flex gap-4">
                                <div className="flex items-center justify-start gap-2 w-full">
                                    <label className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 min-w-24 mr-1">
                                        {t('duration')} ({t('minutes')})
                                    </label>
                                    <div className="ml-2 relative w-full flex flex-1 rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600">
                                        <input
                                            className="block w-full flex-1 border-1 bg-transparent py-1.5 px-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm/6"
                                            type="number"
                                            value={testDurationMinutes}
                                            onChange={onTestDurationChange}
                                            placeholder="Enter..."
                                            required />
                                    </div>
                                </div>

                                <div className="flex items-center justify-start gap-2 w-full">
                                    <label className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 px-1 mr-1">
                                        {t('testTime')}
                                    </label>
                                    <div className="ml-2 relative w-full flex flex-1 rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600">
                                        <input
                                            className="block w-full flex-1 border-1 bg-transparent py-1.5 px-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm/6"
                                            type="datetime-local"
                                            value={testDateTime}
                                            onChange={onTestDateTimeChange}
                                            lang="en-GB" />
                                    </div>
                                </div>
                            </div>

                            {testData === null && (
                                <>

                                    <div className="mt-4 flex items-center justify-center p-1">
                                        <SimpleToggle enabled={isAutomatedTest} setEnabled={setAutomatedTest} title={t('chooseQuestionsAutomatically')} />
                                    </div>

                                    {isAutomatedTest && (
                                        <div className="flex flex-col items-start justify-start gap-4 w-full bg-gray-100 rounded-lg p-4">
                                            <div className="flex items-center justify-start gap-4 px-4 w-full">
                                                <div className="flex items-center justify-center w-full">
                                                    <label className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 px-1 mr-1">
                                                        {t('coverEntireSyllabus')}
                                                    </label>
                                                    <input
                                                        type="checkbox"
                                                        checked={isEntireSyllabus}
                                                        onChange={() => setEntireSyllabus(prev => !prev)}
                                                    />
                                                </div>

                                                <div className="flex items-center justify-center w-full">
                                                    <label className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 px-1 mr-1">
                                                        {t('excludePreviousTestQuestions')}
                                                    </label>
                                                    <input
                                                        type="checkbox"
                                                        checked={isExcludePrevTests}
                                                        onChange={() => setExcludePrevTests(prev => !prev)}
                                                    />
                                                </div>
                                            </div>

                                            {!isEntireSyllabus && (
                                                <div className="flex flex-col items-center justify-start gap-2 w-full">
                                                    <SyllabusFilter subjects={subjects} setSubjects={setSubjects} selectedSubjects={selectedSubjects} setSelectedSubjects={setSelectedSubjects}
                                                        grades={topicGrades} setGrades={setTopicGrades} selectedGrades={selectedTopicGrades} setSelectedGrades={setSelectedTopicGrades}
                                                        mainTopics={mainTopics} setMainTopics={setMainTopics} selectedMainTopics={selectedMainTopics} setSelectedMainTopics={setSelectedMainTopics}
                                                        topics={topics} setTopics={setTopics} selectedTopics={selectedTopics} setSelectedTopics={setSelectedTopics} user={user} />

                                                    <div className="flex items-center justify-start gap-2 w-full">
                                                        <label className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 px-1 mr-1">
                                                            {t('tags')}
                                                        </label>
                                                        <Dropdown
                                                            className='flex items-center justify-start gap-2 w-full'
                                                            list={tags}
                                                            selectedItems={selectedTags}
                                                            onSelectionToggle={onSelectedTagChange}
                                                            nameField={'tag_name'}
                                                            valueField={'id'}
                                                            isSearchable={true} />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex items-center justify-start gap-2 w-full">
                                                <label className="flex justify-center items-center text-sm font-medium leading-6 text-gray-900 min-w-24 mr-1">
                                                    {t('difficulty')}
                                                </label>
                                                <span className="text-sm font-semibold text-white bg-[var(--PrimaryColor)] rounded-md w-12 py-1.5">{testDifficulty}</span>
                                                <span className="text-sm font-medium">{t('Easy')}(0)</span>
                                                <input className="flex-1 text-sm p-2 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm/6 cursor-pointer"
                                                    type="range"
                                                    id="slider"
                                                    min={0}
                                                    max={1}
                                                    step="0.01"
                                                    value={testDifficulty}
                                                    onChange={(event) => setTestDifficulty(event.target.value)}
                                                    required />
                                                <span className="text-sm font-medium">{t('Hard')}(1)</span>
                                            </div>

                                            <AutoTestCreate subjectsData={subjectsData} setSubjectsData={setSubjectsData} sectionsData={sectionsData} setSectionsData={setSectionsData} isEntireSyllabus={isEntireSyllabus} subjects={subjects} setSubjects={setSubjects} selectedSubjects={selectedSubjects} selectedMainTopics={selectedMainTopics} selectedTopics={selectedTopics} />

                                        </div>
                                    )}
                                </>
                            )}

                            <div className="flex items-center justify-center">
                                <button className="mt-2 flex justify-center items-center space-x-2 rounded-md bg-[var(--PrimaryColor)] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-[var(--SecondaryColor)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--PrimaryColor)]"
                                    id='submit' onClick={handleSubmit}>
                                    <span>
                                        {testData ? t('save') : t('create')}
                                    </span>
                                    <ArrowUpTrayIcon aria-hidden="true" className="h-4 w-4 text-white" />
                                </button>
                            </div>
                        </form>
                    </div>

                </div>
            </div>
        )
    }

    return (

        <Dialog open={isOpen} onClose={onRequestClose} className="relative z-20">

            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-20 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">

                    <DialogPanel
                        transition
                        className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >

                        <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                            <button
                                type="button"
                                onClick={onRequestClose}
                                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[var(--PrimaryColor)] focus:ring-offset-2"
                            >
                                <span className="sr-only">{t('closeCapitalized')}</span>
                                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                            </button>
                        </div>

                        {isSubmitted ?
                            (
                                <div>
                                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                        <CheckIcon aria-hidden="true" className="h-6 w-6 text-green-600" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                            {/* {type.id === 'teacher' ? t('newTeacher') : t('newStudent')} */}
                                        </DialogTitle>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                {/* {type.id === 'teacher' ? t('newTeacherRegisteredSuffix') : t('newStudentRegisteredSuffix')} */}
                                            </p>
                                        </div>
                                    </div>
                                </div>)
                            : (
                                <div>
                                    {renderTestCreate()}
                                </div>
                            )}

                    </DialogPanel>
                </div>
            </div>

        </Dialog>
    );
}

export default TestUpsert;
